
import { APP_WILL_MOUNT, APP_WILL_UNMOUNT } from '../base/app';
import {
    CONFERENCE_JOINED,
    CONFERENCE_WILL_LEAVE,
    endpointMessageReceived,
    getCurrentConference
} from '../base/conference';
import { openDialog } from '../base/dialog';
import {
    MHConferenceErrors,
    MHConferenceEvents
} from '../base/lib-meet-hour';
import { setActiveModalId } from '../base/modal';
import {
    getLocalParticipant,
    getParticipantById,
    getParticipantDisplayName
} from '../base/participants';
import { MiddlewareRegistry, StateListenerRegistry } from '../base/redux';
import { playSound, registerSound, unregisterSound } from '../base/sounds';
import { openDisplayNamePrompt } from '../display-name';
import { addGif } from '../gifs/actions';
import { GIF_DEFAULT_TIMEOUT, GIF_PREFIX } from '../gifs/constants';
import { getGifDisplayMode, isGifMessage } from '../gifs/function.any';
import { ADD_REACTION_MESSAGE } from '../reactions/actionTypes';
import { pushReactions } from '../reactions/actions.any';
import { ENDPOINT_REACTION_NAME } from '../reactions/constants';
import { getReactionMessageFromBuffer, getValidReactions, isReactionsEnabled } from '../reactions/functions.any';
import { showToolbox } from '../toolbox/actions';

import { ADD_MESSAGE, CLOSE_CHAT, DELETE_MESSAGE, OPEN_CHAT, OPEN_POLLS, RESTORE_MESSAGE, SEND_EDIT_MESSAGE, SEND_MESSAGE } from './actionTypes';
import { addMessage, clearMessages, deleteChatMessage, editMessage, restoreChatMessage } from './actions';
import { closeChat } from './actions.any';
import { ChatPrivacyDialog } from './components';
import {
    CHAT_VIEW_MODAL_ID,
    INCOMING_MSG_SOUND_ID,
    MESSAGE_TYPE_ERROR,
    MESSAGE_TYPE_LOCAL,
    MESSAGE_TYPE_REMOTE
} from './constants';
import { getUnreadCount } from './functions';
import { INCOMING_MSG_SOUND_FILE } from './sounds';


declare var APP: Object;

/**
 * Timeout for when to show the privacy notice after a private message was received.
 *
 * E.g. If this value is 20 secs (20000ms), then we show the privacy notice when sending a non private
 * message after we have received a private message in the last 20 seconds.
 */
const PRIVACY_NOTICE_TIMEOUT = 20 * 1000;

/**
 * Implements the middleware of the chat feature.
 *
 * @param {Store} store - The redux store.
 * @returns {Function}
 */
MiddlewareRegistry.register(store => next => action => {
    const { dispatch, getState } = store;
    const localParticipant = getLocalParticipant(getState());
    let isOpen, unreadCount;

    switch (action.type) {
    case ADD_MESSAGE:
        unreadCount = getUnreadCount(getState());
        if (action.isReaction) {
            action.hasRead = false;
        } else {
            unreadCount = action.hasRead ? 0 : unreadCount + 1;
        }
        isOpen = getState()['features/chat'].isOpen;

        if (typeof APP !== 'undefined') {
            APP.API.notifyChatUpdated(unreadCount, isOpen);
        }
        break;

    case APP_WILL_MOUNT:
        dispatch(
                registerSound(INCOMING_MSG_SOUND_ID, INCOMING_MSG_SOUND_FILE));
        break;

    case APP_WILL_UNMOUNT:
        dispatch(unregisterSound(INCOMING_MSG_SOUND_ID));
        break;

    case CONFERENCE_JOINED:
        _addChatMsgListener(action.conference, store);
        break;

    case CONFERENCE_WILL_LEAVE:
        _removeChatMsgListeners(action.conference);
        break;

    case OPEN_CHAT:
        if (localParticipant.name || getState()['features/base/settings'].displayName) {
            dispatch(setActiveModalId(CHAT_VIEW_MODAL_ID));
            _maybeFocusField();
        } else {
            dispatch(openDisplayNamePrompt(() => {
                dispatch(setActiveModalId(CHAT_VIEW_MODAL_ID));
                _maybeFocusField();
            }));
        }

        unreadCount = 0;

        if (typeof APP !== 'undefined') {
            APP.API.notifyChatUpdated(unreadCount, true);
        }
        break;

    case OPEN_POLLS:
        if (localParticipant.name || getState()['features/base/settings'].displayName) {
            dispatch(setActiveModalId(CHAT_VIEW_MODAL_ID));
        }
        break;
    case CLOSE_CHAT:
        unreadCount = 0;

        if (typeof APP !== 'undefined') {
            APP.API.notifyChatUpdated(unreadCount, true);
        }

        dispatch(setActiveModalId());
        break;

    case SEND_MESSAGE: {
        const state = store.getState();
        const { conference } = state['features/base/conference'];

        if (conference) {
            // There may be cases when we intend to send a private message but we forget to set the
            // recipient. This logic tries to mitigate this risk.
            const isPrivateChatDisabled = state['features/chat']?.isPrivateChatDisabled;
            const shouldSendPrivateMessageTo = !isPrivateChatDisabled && _shouldSendPrivateMessageTo(state, action);

            if (shouldSendPrivateMessageTo) {
                dispatch(openDialog(ChatPrivacyDialog, {
                    message: action.message,
                    participantID: shouldSendPrivateMessageTo
                }));
            } else {
                // Sending the message if privacy notice doesn't need to be shown.

                const { privateMessageRecipient } = state['features/chat'];

                if (typeof APP !== 'undefined') {
                    APP.API.notifySendingChatMessage(action.message, Boolean(privateMessageRecipient && !isPrivateChatDisabled));
                }

                if (privateMessageRecipient && !isPrivateChatDisabled) {
                    const messageId = `private-${Date.now()}-${Math.random()
                        .toString(36)
                        .substring(2, 11)}`;

                    conference.sendPrivateTextMessage(
                        privateMessageRecipient.id,
                        action.message,
                        'body',
                        undefined,
                        messageId
                    );
                    _persistSentPrivateMessage(store, privateMessageRecipient.id, action.message, messageId);
                } else {
                    conference.sendTextMessage(action.message);
                }
            }
        }
        break;
    }

    case SEND_EDIT_MESSAGE: {
        const state = store.getState();
        const { conference } = state['features/base/conference'];

        if (conference && action.messageId && action.message !== undefined) {
            if (action.recipientId) {
                conference.sendPrivateTextMessage(
                    action.recipientId,
                    action.message,
                    'body',
                    action.messageId
                );

                // Private messages are not echoed back, so optimistically update the sender's UI
                const messages = state['features/chat'].messages || [];
                const existing = messages.find(m => m.messageId === action.messageId);

                if (existing) {
                    dispatch(editMessage({
                        ...existing,
                        message: action.message,
                        messageId: action.messageId,
                        isEdited: true
                    }));
                }
            } else {
                conference.sendTextMessage(action.message, 'body', action.messageId);
            }
        }
        break;
    }
    case ADD_REACTION_MESSAGE: {

        _handleReceivedMessage(store, {
            id: localParticipant.id,
            message: action.message,
            privateMessage: false,
            timestamp: Date.now()
        }, false, true);
        break;
    }


    }

    return next(action);
});

/**
 * Set up state change listener to perform maintenance tasks when the conference
 * is left or failed, e.g. Clear messages or close the chat modal if it's left
 * open.
 */
StateListenerRegistry.register(
    state => getCurrentConference(state),
    (conference, { dispatch, getState }, previousConference) => {
        if (conference !== previousConference) {
            // conference changed, left or failed...

            if (getState()['features/chat'].isOpen) {
                // Closes the chat if it's left open.
                dispatch(closeChat());
            }

            // Clear chat messages.
            dispatch(clearMessages());
        }
    });

StateListenerRegistry.register(
    state => state['features/chat'].isOpen,
    (isOpen, { dispatch }) => {
        if (typeof APP !== 'undefined' && isOpen) {
            dispatch(showToolbox());
        }
    }
);

/**
 * Registers listener for {@link MHConferenceEvents.MESSAGE_RECEIVED} that
 * will perform various chat related activities.
 *
 * @param {MHConference} conference - The conference instance on which the
 * new event listener will be registered.
 * @param {Object} store - The redux store object.
 * @private
 * @returns {void}
 */
let _chatHandlers = null;

/**
 * Conference_addChatMsgListener registers the listeners for chat messages on the conference instance.
 * @param {*} conference
 * @param {*} store
 * @returns
 */
function _addChatMsgListener(conference, store) {

    if (store.getState()['features/base/config'].iAmRecorder) {
        // We don't register anything on web if we are in iAmRecorder mode
        return;
    }


    const onMessageReceived = (...args) => {
        const [ id, message, timestamp, displayName, isGuest, messageId, replaceId ] = args;

        _onConferenceMessageReceived(store, {
            id: id || displayName,
            message,
            messageId,
            replaceId,
            timestamp,
            displayName,
            isGuest,
            privateMessage: false });
    };


    const onPrivateMessageReceived = (...args) => {
        const [ id, message, timestamp, , messageId, replaceId ] = args;

        if (replaceId) {
            if (message === '__MEETHOUR_DELETED__') {
                store.dispatch(deleteChatMessage(replaceId));

                return;
            }

            if (message === '__MEETHOUR_RESTORED__') {
                store.dispatch(restoreChatMessage(replaceId));

                return;
            }

            const state = store.getState();
            const messages = state['features/chat'].messages || [];
            const existing = messages.find(m => m.messageId === replaceId);

            if (existing) {
                store.dispatch(editMessage({
                    ...existing,
                    message,
                    messageId: replaceId,
                    newMessageId: messageId,
                    isEdited: true
                }));
            }

            return;
        }

        if (message === '__MEETHOUR_DELETED__' || message === '__MEETHOUR_RESTORED__') {
            return;
        }

        _handleReceivedMessage(store, {
            id,
            message,
            messageId,
            privateMessage: true,
            timestamp
        });
    };

    const onEndpointMessageReceived = (...args) => {
        const state = store.getState();

        store.dispatch(endpointMessageReceived(...args));

        if (args && args.length >= 2) {
            const [ { _id }, eventData ] = args;

            if (eventData.name === ENDPOINT_REACTION_NAME) {
                const reactions = getValidReactions(eventData.reactions);

                if (!isReactionsEnabled(state) || !reactions.length) {
                    return;
                }
                store.dispatch(pushReactions(reactions));

                _handleReceivedMessage(store, {
                    id: _id,
                    message: getReactionMessageFromBuffer(reactions),
                    privateMessage: false,
                    timestamp: eventData.timestamp
                }, false, true);
            } else if (eventData.name === 'DELETE_MESSAGE' || eventData.type === DELETE_MESSAGE) {
                if (eventData.messageId) {
                    store.dispatch(deleteChatMessage(eventData.messageId, eventData.deletedAt));
                }
            } else if (eventData.name === 'RESTORE_MESSAGE' || eventData.type === RESTORE_MESSAGE) {
                if (eventData.messageId) {
                    store.dispatch(restoreChatMessage(eventData.messageId));
                }
            }
        }
    };

    const onConferenceError = (errorType, error) => {
        errorType === MHConferenceErrors.CHAT_ERROR && _handleChatError(store, error);
    };

    _chatHandlers = {
        conference,
        onMessageReceived,
        onPrivateMessageReceived,
        onEndpointMessageReceived,
        onConferenceError
    };

    conference.on(MHConferenceEvents.MESSAGE_RECEIVED, onMessageReceived);
    conference.on(MHConferenceEvents.PRIVATE_MESSAGE_RECEIVED, onPrivateMessageReceived);
    conference.on(MHConferenceEvents.ENDPOINT_MESSAGE_RECEIVED, onEndpointMessageReceived);
    conference.on(MHConferenceEvents.CONFERENCE_ERROR, onConferenceError);
}

/**
 * Removes chat message listeners from the conference.
 *
 * @param {MHConference} conference - The conference instance.
 * @private
 * @returns {void}
 */
function _removeChatMsgListeners(conference) {
    if (_chatHandlers) {
        const c = conference || _chatHandlers.conference;

        c.off(MHConferenceEvents.MESSAGE_RECEIVED, _chatHandlers.onMessageReceived);
        c.off(MHConferenceEvents.PRIVATE_MESSAGE_RECEIVED, _chatHandlers.onPrivateMessageReceived);
        c.off(MHConferenceEvents.ENDPOINT_MESSAGE_RECEIVED, _chatHandlers.onEndpointMessageReceived);
        c.off(MHConferenceEvents.CONFERENCE_ERROR, _chatHandlers.onConferenceError);
        _chatHandlers = null;
    }
}

/**
 * Handles a chat error received from the xmpp server.
 *
 * @param {Store} store - The Redux store.
 * @param  {string} error - The error message.
 * @returns {void}
 */
function _handleChatError({ dispatch }, error) {
    dispatch(addMessage({
        hasRead: true,
        messageType: MESSAGE_TYPE_ERROR,
        message: error,
        privateMessage: false,
        timestamp: Date.now()
    }));
}

/**
 * Function to handle an incoming chat message.
 *
 * @param {Store} store - The Redux store.
 * @param {Object} message - The message object.
 * @param {boolean} shouldPlaySound - Whether or not to play the incoming message sound.
 * @param {boolean} isReaction - Whether or not the message is a reaction message.
 * @returns {void}
 */
function _handleReceivedMessage({ dispatch, getState }, { id, message, messageId, privateMessage, timestamp }, shouldPlaySound = true,
        isReaction = false) {
    if (message === '__MEETHOUR_DELETED__' || message === '__MEETHOUR_RESTORED__') {
        return;
    }

    // Logic for all platforms:
    const state = getState();
    const { deletedMessageIds = [] } = state['features/chat'] || {};
    const isDeleted = Boolean(messageId && deletedMessageIds.includes(messageId));
    const { isOpen: isChatOpen } = state['features/chat'];
    const { iAmRecorder } = state['features/base/config'];

    // const { soundsIncomingMessage: soundEnabled } = state['features/base/settings'];

    if (shouldPlaySound && !isChatOpen && !isDeleted) {
        dispatch(playSound(INCOMING_MSG_SOUND_ID));
    }


    // Provide a default for for the case when a message is being
    // backfilled for a participant that has left the conference.
    const participant = getParticipantById(state, id) || {};
    const localParticipant = getLocalParticipant(getState);
    const displayName = getParticipantDisplayName(state, id);
    const hasRead = participant.local || isChatOpen;
    const timestampToDate = timestamp ? new Date(timestamp) : new Date();
    const millisecondsTimestamp = timestampToDate.getTime();

    dispatch(addMessage({
        displayName,
        hasRead,
        id,
        messageId,
        messageType: participant.local ? MESSAGE_TYPE_LOCAL : MESSAGE_TYPE_REMOTE,
        message,
        privateMessage,
        recipient: getParticipantDisplayName(state, localParticipant?.id),
        timestamp: millisecondsTimestamp,
        isReaction,
        deleteChat: isDeleted
    }));

    if (typeof APP !== 'undefined' && !isDeleted) {
        // Logic for web only:

        APP.API.notifyReceivedChatMessage({
            body: message,
            id,
            nick: displayName,
            privateMessage,
            ts: timestamp
        });

        if (!iAmRecorder && shouldPlaySound) {
            dispatch(showToolbox(4000));
        }

    }
}

/**
 * Focuses the chat text field on web after the message recipient was updated, if needed.
 *
 * @returns {void}
 */
function _maybeFocusField() {
    if (navigator.product !== 'ReactNative') {
        const textField = document.getElementById('usermsg');

        textField && textField.focus();
    }
}

/**
 * Handles a received message. Supports XEP-0308 (message correction) via replaceId.
 *
 * @param {Object} store - Redux store.
 * @param {Object} payload - The message payload (id, message, messageId, replaceId, timestamp, etc.).
 * @returns {void}
 */
function _onConferenceMessageReceived(store, { displayName, id, isGuest, message, messageId, replaceId, timestamp, privateMessage }) {
    const isGif = isGifMessage(message);

    if (isGif) {
        const displayMode = getGifDisplayMode(store.getState());

        if (displayMode !== 'chat') {
            _handleGifMessageReceived(store, id, message, timestamp);
        }

        if (displayMode === 'tile') {
            return;
        }
    }

    if (replaceId) {
        if (message === '__MEETHOUR_DELETED__') {
            store.dispatch(deleteChatMessage(replaceId));

            return;
        }

        if (message === '__MEETHOUR_RESTORED__') {
            store.dispatch(restoreChatMessage(replaceId));

            return;
        }

        const state = store.getState();
        const messages = state['features/chat'].messages || [];
        const existing = messages.find(m => m.messageId === replaceId);

        if (existing) {
            store.dispatch(editMessage({
                ...existing,
                message,
                messageId: replaceId,
                newMessageId: messageId,
                isEdited: true
            }));
        }

        return;
    }

    if (message === '__MEETHOUR_DELETED__' || message === '__MEETHOUR_RESTORED__') {
        return;
    }

    const hasDelay = Boolean(timestamp);
    const messageTime = hasDelay ? new Date(timestamp).getTime() : NaN;
    const isOldMessage = hasDelay && !isNaN(messageTime) && (Date.now() - messageTime > 10000);

    _handleReceivedMessage(store, {
        displayName,
        id,
        isGuest,
        message,
        messageId,
        privateMessage,
        lobbyChat: false,
        timestamp
    }, !isOldMessage, isGif);
}

/**
 * Handles a received gif message.
 *
 * @param {Object} store - Redux store.
 * @param {string} id - Id of the participant that sent the message.
 * @param {string} message - The message sent.
 * @param {string|number|undefined} timestamp - The timestamp when the message was sent.
 * @returns {void}
 */
function _handleGifMessageReceived(store, id, message, timestamp) {
    if (timestamp) {
        const messageTime = new Date(timestamp).getTime();

        if (!isNaN(messageTime)) {
            const { giphy } = store.getState()['features/base/config'] || {};
            const tileTime = giphy?.tileTime || GIF_DEFAULT_TIMEOUT;
            const age = Date.now() - messageTime;

            if (age >= tileTime) {
                return;
            }

            const remainingTimeout = tileTime - Math.max(0, age);
            const url = message.substring(GIF_PREFIX.length, message.length - 1);

            store.dispatch(addGif(id, url, remainingTimeout));

            return;
        }
    }

    const url = message.substring(GIF_PREFIX.length, message.length - 1);

    store.dispatch(addGif(id, url));
}

/**
 * Persists the sent private messages as if they were received over the muc.
 *
 * This is required as we rely on the fact that we receive all messages from the muc that we send
 * (as they are sent to everybody), but we don't receive the private messages we send to another participant.
 * But those messages should be in the store as well, otherwise they don't appear in the chat window.
 *
 * @param {Store} store - The Redux store.
 * @param {string} recipientID - The ID of the recipient the private message was sent to.
 * @param {string} message - The sent message.
 * @returns {void}
 */
function _persistSentPrivateMessage({ dispatch, getState }, recipientID, message, messageId) {
    const localParticipant = getLocalParticipant(getState);
    const displayName = getParticipantDisplayName(getState, localParticipant.id);

    dispatch(addMessage({
        displayName,
        hasRead: true,
        id: localParticipant.id,
        messageId,
        messageType: MESSAGE_TYPE_LOCAL,
        message,
        privateMessage: true,
        recipient: getParticipantDisplayName(getState, recipientID),
        recipientId: recipientID,
        timestamp: Date.now(),
        deleteChat: false
    }));
}

/**
 * Returns the ID of the participant who we may have wanted to send the message
 * that we're about to send.
 *
 * @param {Object} state - The Redux state.
 * @param {Object} action - The action being dispatched now.
 * @returns {string?}
 */
function _shouldSendPrivateMessageTo(state, action): ?string {
    if (action.ignorePrivacy) {
        // Shortcut: this is only true, if we already displayed the notice, so no need to show it again.
        return undefined;
    }

    const { messages, privateMessageRecipient } = state['features/chat'];

    if (privateMessageRecipient) {
        // We're already sending a private message, no need to warn about privacy.
        return undefined;
    }

    if (!messages.length) {
        // No messages yet, no need to warn for privacy.
        return undefined;
    }

    // Platforms sort messages differently
    const lastMessage = navigator.product === 'ReactNative'
        ? messages[0] : messages[messages.length - 1];

    if (lastMessage.messageType === MESSAGE_TYPE_LOCAL) {
        // The sender is probably aware of any private messages as already sent
        // a message since then. Doesn't make sense to display the notice now.
        return undefined;
    }

    if (lastMessage.privateMessage) {
        // We show the notice if the last received message was private.
        return lastMessage.id;
    }

    // But messages may come rapidly, we want to protect our users from mis-sending a message
    // even when there was a reasonable recently received private message.
    const now = Date.now();
    const recentPrivateMessages = messages.filter(
        message =>
            message.messageType !== MESSAGE_TYPE_LOCAL
            && message.privateMessage
            && message.timestamp + PRIVACY_NOTICE_TIMEOUT > now);
    const recentPrivateMessage = navigator.product === 'ReactNative'
        ? recentPrivateMessages[0] : recentPrivateMessages[recentPrivateMessages.length - 1];

    if (recentPrivateMessage) {
        return recentPrivateMessage.id;
    }

    return undefined;
}
